// AdMob (Google Mobile Ads) via @capacitor-community/admob 8.x.
//
// ORDEM DE CONSENTIMENTO — só na PRIMEIRA vez que um anúncio seria exibido,
// nunca na abertura do app:
//   1. initialize (classificação de conteúdo conservadora, vinda da config)
//   2. UMP (formulário de consentimento do Google, quando exigido)
//   3. ATT (iOS: "permitir rastreamento?")
// Sem consentimento OBTIDO, os anúncios saem NÃO PERSONALIZADOS (npa).
//
// NENHUM DADO DE SAÚDE VAI AO SDK: sem palavras-chave, sem contentUrl, sem
// segmentação. O SDK recebe só o id do bloco e, no rewarded, o uid e o id da
// sessão (para o SSV).
//
// IDs: os REAIS vêm da config remota (CONFIG/ANUNCIOS.AD_UNITS). Os de TESTE do
// Google são usados em QUALQUER build que não seja de loja:
//   - bundle web de desenvolvimento (npm start, NODE_ENV !== production);
//   - build nativo de DEBUG — APK debuggable no Android, configuração Debug no
//     Xcode. O Capacitor injeta `window.Capacitor.DEBUG` a partir do próprio
//     binário (Android: FLAG_DEBUGGABLE; iOS: #if DEBUG). Isso importa porque o
//     APK de debug empacota o bundle de PRODUÇÃO (cap:sync faz npm run build):
//     só o NODE_ENV não bastava, e anúncio real em aparelho de teste pode
//     suspender a conta do AdMob;
//   - REACT_APP_ADMOB_TESTE=1 (força teste num build de release interno).
// Release de loja/TestFlight ⇒ IDs reais. Sem ID real na config ⇒ não mostra.
//
// Aparelhos de teste: REACT_APP_ADMOB_TEST_DEVICES (ids separados por vírgula)
// vai para initialize({testingDevices}). O caminho que vale também para build
// de loja, sem código, é cadastrar o aparelho em AdMob → Configurações →
// Dispositivos de teste (pelo ID de publicidade).

import { Capacitor } from '@capacitor/core';
import { registrarMetrica, registrarReceita } from './anunciosApi';

const TESTE = {
  android: {
    INTERSTICIAL: 'ca-app-pub-3940256099942544/1033173712',
    REWARDED: 'ca-app-pub-3940256099942544/5224354917',
  },
  ios: {
    INTERSTICIAL: 'ca-app-pub-3940256099942544/4411468910',
    REWARDED: 'ca-app-pub-3940256099942544/1712485313',
  },
};

const RATING = { G: 'General', PG: 'ParentalGuidance', T: 'Teen', MA: 'MatureAudience' };

let _mod = null; // import dinâmico: web e testes nunca carregam o plugin
let _pronto = null; // Promise da inicialização + consentimento
let _npa = true;
let _intersticialCarregado = false;
let _carregandoIntersticial = null;
let _listenersReceita = false;
let _prontoOk = false; // garantirPronto já terminou E liberou pedir anúncio
let _silencioso = null; // Promise da inicialização SEM UI (initialize + info)
let _consentInfo = null; // último requestConsentInfo
let _rewardedPronto = null; // { adId, ssvChave } do rewarded já carregado
let _carregandoRewarded = null;

export function buildNativoDeDebug() {
  try {
    return typeof window !== 'undefined' && window.Capacitor?.DEBUG === true;
  } catch {
    return false;
  }
}

export function usarIdsDeTeste() {
  return process.env.NODE_ENV !== 'production'
    || process.env.REACT_APP_ADMOB_TESTE === '1'
    || buildNativoDeDebug();
}

function aparelhosDeTeste() {
  return String(process.env.REACT_APP_ADMOB_TEST_DEVICES || '')
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
}

export function nativo() {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('AdMob');
}

function plataforma() {
  return Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';
}

/** Id do bloco (real da config ou de teste). null ⇒ não há o que mostrar. */
export function idDoBloco(cfg, formato) {
  const plat = plataforma();
  if (usarIdsDeTeste()) return TESTE[plat][formato];
  const chave = plat === 'ios' ? 'IOS' : 'ANDROID';
  const real = cfg?.AD_UNITS?.[chave]?.[formato];
  return typeof real === 'string' && /^ca-app-pub-\d+\/\d+$/.test(real) ? real : null;
}

async function plugin() {
  if (!_mod) _mod = await import('@capacitor-community/admob');
  return _mod;
}

function comTimeout(promessa, ms, valor = null) {
  return Promise.race([
    promessa,
    new Promise((resolve) => setTimeout(() => resolve(valor), ms)),
  ]);
}

async function ligarListenersDeReceita(AdMob, mod) {
  if (_listenersReceita) return;
  _listenersReceita = true;
  try {
    await AdMob.addListener(mod.InterstitialAdPluginEvents.AdImpression,
      (d) => registrarReceita('INTERSTICIAL', d));
    await AdMob.addListener(mod.RewardAdPluginEvents.AdImpression,
      (d) => registrarReceita('REWARDED', d));
  } catch {
    /* sem receita por impressão: o painel usa a estimativa por eCPM */
  }
}

/**
 * FASE SILENCIOSA: inicializa o SDK e consulta o estado do consentimento.
 *
 * Não abre NADA. `initialize` e `requestConsentInfo` não têm UI — só
 * `showConsentForm` e `requestTrackingAuthorization` têm, e esses ficam em
 * `garantirPronto`. É esta separação que permite carregar um anúncio ANTES da
 * hora de exibir sem risco de um formulário de consentimento saltar por cima
 * da animação de celebração.
 * @param {object} cfg config pública de anúncios
 * @return {Promise<boolean>} true se já dá para PEDIR inventário
 */
export function prepararSilencioso(cfg) {
  if (!nativo()) return Promise.resolve(false);
  if (_silencioso) return _silencioso;
  _silencioso = (async () => {
    const { AdMob } = await plugin();
    await AdMob.initialize({
      maxAdContentRating: RATING[cfg?.MAX_AD_CONTENT_RATING] || 'General',
      tagForChildDirectedTreatment: false,
      tagForUnderAgeOfConsent: false,
      initializeForTesting: usarIdsDeTeste(),
      testingDevices: aparelhosDeTeste(),
    });
    try {
      _consentInfo = await AdMob.requestConsentInfo();
    } catch {
      _consentInfo = null;
    }
    // Sem consentimento OBTIDO o anúncio sai não personalizado — o que é
    // legítimo e carregável. O que NÃO dá é pedir quando o UMP diz que não
    // pode (EEE com formulário pendente): aí só depois de garantirPronto.
    _npa = cfg?.NPA_SEM_CONSENTIMENTO !== false && _consentInfo?.status !== 'OBTAINED';
    if (!_consentInfo) return true;
    if (_consentInfo.canRequestAds === false) return false;
    return true;
  })().catch(() => {
    _silencioso = null;
    return false;
  });
  return _silencioso;
}

/** Ainda falta mostrar o formulário do UMP para esta pessoa? */
function formularioPendente() {
  return !!(_consentInfo?.isConsentFormAvailable && _consentInfo.status === 'REQUIRED');
}

/**
 * Inicializa o SDK e pede consentimento — uma vez por execução do app.
 * Esta é a fase INTERATIVA: pode abrir o formulário do UMP e o ATT.
 * @param {object} cfg config pública de anúncios
 * @return {Promise<boolean>} true se dá para pedir anúncios
 */
export function garantirPronto(cfg) {
  if (!nativo()) return Promise.resolve(false);
  if (_pronto) return _pronto;
  _pronto = (async () => {
    await prepararSilencioso(cfg);
    const mod = await plugin();
    const { AdMob } = mod;

    let consentimento = _consentInfo;
    try {
      if (formularioPendente()) consentimento = await AdMob.showConsentForm();
      if (consentimento?.status === 'OBTAINED') registrarMetrica('CONSENTIMENTO_OBTIDO');
      else if (consentimento?.status === 'REQUIRED') registrarMetrica('CONSENTIMENTO_NEGADO');
    } catch {
      consentimento = null;
    }

    if (plataforma() === 'ios') {
      try {
        const { status } = await AdMob.trackingAuthorizationStatus();
        if (status === 'notDetermined') {
          await AdMob.requestTrackingAuthorization();
          const depois = await AdMob.trackingAuthorizationStatus();
          registrarMetrica(depois.status === 'authorized' ? 'ATT_AUTORIZADO' : 'ATT_NEGADO');
        }
      } catch {
        /* ATT indisponível: segue não personalizado */
      }
    }

    if (consentimento) _consentInfo = consentimento;
    _npa = cfg?.NPA_SEM_CONSENTIMENTO !== false && consentimento?.status !== 'OBTAINED';
    await ligarListenersDeReceita(AdMob, mod);
    // UMP diz se dá para pedir anúncio (ex.: EEE sem consentimento ⇒ false).
    const pode = consentimento ? consentimento.canRequestAds !== false : true;
    _prontoOk = pode;
    return pode;
  })().catch(() => {
    _pronto = null;
    _prontoOk = false;
    return false;
  });
  return _pronto;
}

/**
 * AQUECE o inventário sem abrir nada: fase silenciosa + carga do intersticial.
 * Chamar isto cedo (enquanto a celebração anima) é o que faz o anúncio
 * aparecer na hora em vez de a tela esperar uma carga a frio.
 * @param {object} cfg config pública
 * @return {Promise<boolean>} true se há intersticial pronto
 */
export async function aquecer(cfg) {
  if (!nativo()) return false;
  const pode = await prepararSilencioso(cfg);
  if (!pode) return false;
  return !!(await preCarregarIntersticial(cfg));
}

/** Pré-carrega o próximo intersticial (silencioso). */
export function preCarregarIntersticial(cfg) {
  // Já carregado (e nada em voo) devolvia `null`, e quem encadeasse .catch()
  // no retorno estourava. Sempre uma Promise.
  if (_intersticialCarregado) return Promise.resolve(_carregandoIntersticial || true);
  if (_carregandoIntersticial) return _carregandoIntersticial;
  const adId = idDoBloco(cfg, 'INTERSTICIAL');
  if (!adId || !nativo()) return Promise.resolve(false);
  _carregandoIntersticial = (async () => {
    const { AdMob } = await plugin();
    await AdMob.prepareInterstitial({ adId, npa: _npa, isTesting: usarIdsDeTeste() });
    _intersticialCarregado = true;
    return true;
  })()
    .catch(() => false)
    .finally(() => {
      _carregandoIntersticial = null;
    });
  return _carregandoIntersticial;
}

/**
 * Mostra um intersticial. Resolve true quando ele apareceu e foi fechado.
 * @param {object} cfg config pública
 * @return {Promise<boolean>}
 */
export async function mostrarIntersticial(cfg) {
  if (!nativo()) return false;
  const podePedir = await comTimeout(garantirPronto(cfg), 15000, false);
  if (!podePedir) return false;
  if (!_intersticialCarregado) {
    // Caminho de EXCEÇÃO: o normal é `anteciparDecisao` já ter pré-carregado
    // enquanto a celebração animava. Quando cai aqui, a carga é a frio e 4s
    // não davam — o anúncio ficava pronto logo depois de o slot ter desistido.
    const ok = await comTimeout(preCarregarIntersticial(cfg), 9000, false);
    if (!ok) return false;
  }
  const mod = await plugin();
  const { AdMob, InterstitialAdPluginEvents } = mod;
  const handles = [];
  const resultado = await new Promise((resolve) => {
    let fim = false;
    const terminar = (v) => {
      if (fim) return;
      fim = true;
      resolve(v);
    };
    let abriu = false;
    Promise.all([
      AdMob.addListener(InterstitialAdPluginEvents.Showed, () => { abriu = true; }),
      AdMob.addListener(InterstitialAdPluginEvents.Dismissed, () => terminar(true)),
      AdMob.addListener(InterstitialAdPluginEvents.FailedToShow, () => terminar(false)),
    ]).then((h) => {
      handles.push(...h);
      AdMob.showInterstitial().catch(() => terminar(false));
      // O anúncio que não abre não avisa: no iOS, sem view controller raiz o
      // plugin nem resolve nem rejeita o show. Sem este corte a pessoa ficava
      // 2 min olhando o carregando do slot depois de registrar.
      setTimeout(() => { if (!abriu) terminar(false); }, 12000);
    }).catch(() => terminar(false));
    // Rede de segurança: nenhum anúncio segura a pessoa para sempre.
    setTimeout(() => terminar(true), 120000);
  });
  handles.forEach((h) => h?.remove?.());
  _intersticialCarregado = false;
  // Próximo já vai carregando para a próxima ação.
  setTimeout(() => preCarregarIntersticial(cfg), 1500);
  return resultado;
}

/**
 * O SSV é assado DENTRO do anúncio preparado: um rewarded carregado para uma
 * sessão não serve para outra. A chave amarra a carga à sessão.
 * @param {object} ssv {userId, customData}
 * @return {string}
 */
function chaveSsv(ssv) {
  return `${ssv?.userId || ''}|${ssv?.customData || ''}`;
}

/**
 * Pré-carrega o rewarded desta sessão, silenciosamente.
 *
 * Chamar quando a tela que oferece o vídeo ABRE, não quando a pessoa toca:
 * a carga leva segundos e é ela que fazia o toque parecer travado.
 * @param {object} cfg config pública
 * @param {{userId:string, customData:string}} ssv
 * @return {Promise<boolean>}
 */
export function preCarregarRewarded(cfg, ssv) {
  const adId = idDoBloco(cfg, 'REWARDED');
  if (!adId || !nativo()) return Promise.resolve(false);
  const chave = chaveSsv(ssv);
  if (_rewardedPronto?.chave === chave) return Promise.resolve(true);
  if (_carregandoRewarded?.chave === chave) return _carregandoRewarded.promessa;
  const promessa = (async () => {
    const pode = await prepararSilencioso(cfg);
    if (!pode) return false;
    const { AdMob } = await plugin();
    await AdMob.prepareRewardVideoAd({ adId, npa: _npa, isTesting: usarIdsDeTeste(), ssv });
    _rewardedPronto = { chave, adId };
    return true;
  })()
    .catch(() => false)
    .finally(() => {
      if (_carregandoRewarded?.chave === chave) _carregandoRewarded = null;
    });
  _carregandoRewarded = { chave, promessa };
  return promessa;
}

/**
 * Mostra um rewarded com SSV. A recompensa NÃO sai daqui: sai do servidor,
 * quando o Google chamar o admobSsv. Isto só diz se o vídeo foi até o fim.
 * @param {object} cfg config pública
 * @param {{userId:string, customData:string}} ssv
 * @return {Promise<{exibido:boolean, recompensado:boolean, semAnuncio?:boolean}>}
 */
export async function mostrarRewarded(cfg, ssv) {
  const adId = idDoBloco(cfg, 'REWARDED');
  if (!adId || !nativo()) return { exibido: false, recompensado: false, semAnuncio: true };
  const chave = chaveSsv(ssv);
  // Se a tela aqueceu na abertura, isto já está resolvido e o vídeo abre na
  // hora. Senão, carrega agora — e só aqui o UMP/ATT pode aparecer.
  const jaCarregado = _rewardedPronto?.chave === chave;
  const aquecendo = jaCarregado ? null : preCarregarRewarded(cfg, ssv);
  const podePedir = await comTimeout(garantirPronto(cfg), 15000, false);
  if (!podePedir) return { exibido: false, recompensado: false, semAnuncio: true };
  const mod = await plugin();
  const { AdMob, RewardAdPluginEvents } = mod;
  const carregou = jaCarregado || await comTimeout(aquecendo, 10000, false);
  if (!carregou) {
    registrarMetrica('REWARDED_SEM_FILL');
    return { exibido: false, recompensado: false, semAnuncio: true };
  }
  const handles = [];
  let recompensado = false;
  const exibido = await new Promise((resolve) => {
    let fim = false;
    const terminar = (v) => {
      if (fim) return;
      fim = true;
      resolve(v);
    };
    Promise.all([
      AdMob.addListener(RewardAdPluginEvents.Rewarded, () => {
        recompensado = true;
      }),
      AdMob.addListener(RewardAdPluginEvents.Dismissed, () => terminar(true)),
      AdMob.addListener(RewardAdPluginEvents.FailedToShow, () => terminar(false)),
    ]).then((h) => {
      handles.push(...h);
      // A CHAMADA TAMBÉM ENTREGA A RECOMPENSA. Nos dois nativos o
      // `call.resolve` acontece dentro do MESMO callback que emite o evento
      // Rewarded (iOS: `userDidEarnRewardHandler`; Android:
      // `OnUserEarnedRewardListener`). É o mesmo sinal por um caminho que não
      // depende da ordem de chegada dos eventos no JS.
      AdMob.showRewardVideoAd()
        .then((r) => {
          if (r && (r.type !== undefined || r.amount !== undefined)) recompensado = true;
        })
        .catch(() => {
          /* fechar antes rejeita em algumas versões; o Dismissed decide */
        });
    });
    setTimeout(() => terminar(true), 180000);
  });
  // O Dismissed pode chegar ao JS na frente do Rewarded quando os dois saem
  // juntos do nativo. Uma volta no event loop antes de ler `recompensado`
  // evita acusar de "fechou antes" quem assistiu o vídeo inteiro.
  await new Promise((r) => setTimeout(r, 300));
  handles.forEach((h) => h?.remove?.());
  // Consumido: o nativo já descartou o anúncio ao fechar (removeCurrentlyShowingAd
  // no iOS, preparedAds.remove no Android). Deixar a marca para trás faria a
  // próxima sessão achar que tinha vídeo pronto e mostrar nada.
  if (_rewardedPronto?.chave === chave) _rewardedPronto = null;
  if (exibido && !recompensado) registrarMetrica('REWARDED_FECHOU_ANTES');
  return { exibido, recompensado };
}
